import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Map as ImmutableMap } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import EditForm from 'ContentComponents/cmv2/EditForm';
import H4 from 'UIComponents/elements/headings/H4';
import { updateTemplateEditorForm } from '../../actions/TemplateEditorActions';
import { updateSelectedNavItem } from '../../actions/TemplateEditorNavigationActions';
import { getModuleSpec, getFieldDefinition } from '../../constants/templateEditor/KnowledgeBaseSettingsModule';
import { NAV_ITEMS, SUPPORT_FORM_ID_SETTING_KEY_PATH } from 'knowledge-content-ui/constants/TemplateEditorConstants';
import TemplateEditorFieldMap from '../../config-fields/components/TemplateEditorFieldMap';
import { SCREENS } from '../../constants/TrackingConstants';
import { visibilityOptions } from '../../constants/ArticleVisibilityConstants';
import ManageFiltersRecord from '../../records/manage/ManageFiltersRecord';
import usePrevious from '../../hooks/usePrevious';
import { getIsUngatedForCusomerPortal } from '../../selectors/gates';
import { selectHasCustomerPortal } from '../../selectors/scopes';
import { selectConnectedForms } from '../../selectors/connectedForms';
import { selectKnowledgeBase } from '../../knowledge-base/selectors/knowledgeBaseDetails';
import { selectHasPrivateArticles, selectTemplateSettings, selectIsViewingTemplateEditorLanguageVariation } from '../../selectors/templateSettings';
import { selectCustomerPortalSettings } from '../../selectors/customerPortalSettings';
import { validateField } from '../../utils/templateSettingsValuesAndValidationUtils';
import UsageTracker from '../../utils/UsageTracker';
import ContentFormHeader from './ContentFormHeader';
import TemplateEditorSidebarAlert from './TemplateEditorSidebarAlert';
import ConfirmDeletionModal from './ConfirmDeletionModal';
import { getItem, setItem } from '../../utils/localStorage';
import { syncLanguageLinkTranslations, getProperTranslationKeyPath } from '../../utils/langUtils';
import { uuidv4 } from '../../utils/stringUtils';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// @ts-expect-error ts-migrate(7009) FIXME: 'new' expression, whose target lacks a construct s... Remove this comment to see the full error message
const EMPTY_MAP = new ImmutableMap();
const StyledEditForm = styled(EditForm).withConfig({
  displayName: "TemplateEditorForm__StyledEditForm",
  componentId: "yvec7d-0"
})(["margin-bottom:80px;"]);
const fieldChangeToTrackingAction = {
  footerCompanyLogo(newVal, oldVal) {
    if ((!newVal || !newVal.src) && oldVal && oldVal.src) {
      return 'delete-footer-company-logo';
    } else if (newVal && newVal.src && (!oldVal || !oldVal.src)) {
      return 'add-footer-company-logo';
    }
    return null;
  },
  supportFormId(newVal, oldVal) {
    if (newVal != null && oldVal == null) {
      return 'support-form-enabled';
    } else if (newVal == null && oldVal != null) {
      return 'support-form-disabled';
    }
    return null;
  },
  socialNetworkUrls(newVal, oldVal) {
    if (!newVal && !oldVal) {
      return null;
    }
    if (newVal && oldVal && newVal.length > oldVal.length || !oldVal && newVal.length > 0) {
      return 'footer-add-social-network';
    } else if (newVal && oldVal && newVal.length < oldVal.length || !newVal && oldVal.length > 0) {
      return 'footer-remove-social-network';
    }
    return null;
  },
  headerBackground(newVal, oldVal) {
    if (newVal && oldVal) {
      if ((newVal.backgroundPatternSrc || newVal.backgroundGradientType) && !oldVal.backgroundPatternSrc && !oldVal.backgroundGradientType) {
        return 'header-add-background-pattern';
      }
      if (newVal.backgroundImageSrc && !oldVal.backgroundImageSrc) {
        return 'header-add-header-image';
      }
      if (!newVal.backgroundImageSrc && !newVal.backgroundPatternSrc && !newVal.backgroundGradientType && oldVal.backgroundImageSrc) {
        return 'header-remove-header-image'; // Click remove image button; Don't count removing an image by choosing a pattern
      }
    }
    return null;
  },
  headerCompanyLogo(newVal, oldVal) {
    if ((!newVal || !newVal.src) && oldVal && oldVal.src) {
      return 'delete-company-logo';
    } else if (newVal && newVal.src && (!oldVal || !oldVal.src)) {
      return 'add-company-logo';
    }
    return null;
  }
};
const shouldShowTranslationModal = () => {
  const token = getItem('hideTranslationModal', null);
  if (token) {
    return token !== 'true';
  }
  return true;
};
function TemplateEditorForm(props) {
  const {
    dispatchDebouncedUpdateTemplateSettings,
    fetchHasPrivateArticles,
    fetchCustomerPortalSettings,
    hasHeader,
    masterLanguage,
    selectedNavItem,
    selectedLanguage,
    handleTranslateClick
  } = props;
  const [fieldKey, setFieldKey] = useState(null);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [onDeleteInfo, setOnDeleteInfo] = useState({
    keyPath: null,
    value: null,
    module: null,
    errors: null,
    prevValue: null
  });
  const prevSelectedNavItemType = usePrevious(selectedNavItem.type);

  // Redux stuff
  const connectedForms = useAppSelector(selectConnectedForms);
  const {
    id: knowledgeBaseId,
    indexTemplatePath
  } = useAppSelector(selectKnowledgeBase);
  const shouldShowSignInLinkToggle = !!useAppSelector(selectHasPrivateArticles);
  const shouldShowCustomerPortalLinkToggle = useAppSelector(selectCustomerPortalSettings);
  const templateSettings = useAppSelector(selectTemplateSettings);
  const dispatch = useAppDispatch();
  const isViewingLanguageVariation = useAppSelector(selectIsViewingTemplateEditorLanguageVariation);
  const isUngatedForCustomerPortal = useAppSelector(getIsUngatedForCusomerPortal);
  const hasCustomerPortal = useAppSelector(selectHasCustomerPortal) && isUngatedForCustomerPortal;
  useEffect(() => {
    fetchHasPrivateArticles(new ManageFiltersRecord({
      visibility: visibilityOptions.NOT_PUBLIC
    }));
    if (hasCustomerPortal) fetchCustomerPortalSettings();
  });
  useEffect(() => {
    if ((prevSelectedNavItemType === NAV_ITEMS.EDIT_SOCIAL || prevSelectedNavItemType === NAV_ITEMS.EDIT_FOOTER_LINKS) && selectedNavItem.type === NAV_ITEMS.FOOTER) {
      setFieldKey(null);
    }
  }, [prevSelectedNavItemType, selectedNavItem.type]);
  const handleUpdateTemplateEditorForm = updateData => {
    dispatch(updateTemplateEditorForm(updateData));
    dispatchDebouncedUpdateTemplateSettings(knowledgeBaseId, updateData);
  };
  const trackFieldChange = (field, newVal, oldVal) => {
    const fieldName = field.name;
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const mapper = fieldChangeToTrackingAction[fieldName];
    const action = mapper && mapper(newVal, oldVal && oldVal.toJS ? oldVal.toJS() : oldVal);
    if (action) {
      UsageTracker.track('templateEditorInteraction', {
        screen: SCREENS.TEMPLATE_EDITOR,
        action
      });
    }
  };
  const getModuleValues = () => {
    const options = {
      hasSupportForm: connectedForms.length > 0,
      shouldShowCustomerPortalLinkToggle,
      shouldShowSignInLinkToggle,
      supportFormId: templateSettings.getIn(SUPPORT_FORM_ID_SETTING_KEY_PATH)
    };
    const spec = getModuleSpec(selectedNavItem.type, options);
    const rec = (parent, branchValues = EMPTY_MAP, treeValues = EMPTY_MAP, childrenKey = 'children') => {
      const children = parent[childrenKey];
      if (!children || typeof parent.occurrence !== 'undefined' || Array.isArray(branchValues)) {
        return branchValues;
      }
      let finalValues = branchValues;
      children.forEach(child => {
        const childName = child.name;
        let childData = finalValues.get(childName);
        childData = rec(child, childData, treeValues);
        try {
          finalValues = finalValues.set(child.name, childData);
        } catch (err) {
          if (err.message.includes('Cannot set unknown key')) {
            finalValues = finalValues.toMap().set(child.name, childData);
          } else {
            throw err;
          }
        }
      });
      return finalValues;
    };
    const mappedFormValues = rec(spec, templateSettings, templateSettings, 'fields');
    return mappedFormValues;
  };
  const handleFieldKeyChange = newKey => {
    // "Edit Social" breadcrumb
    if (selectedNavItem.type === NAV_ITEMS.FOOTER && newKey.includes('socialNetworkUrls') && newKey.length === 3) {
      dispatch(updateSelectedNavItem(NAV_ITEMS.EDIT_SOCIAL));
    }

    // "Edit footer links" breadcrumb
    if (selectedNavItem.type === NAV_ITEMS.FOOTER && newKey.includes('footerLinks') && newKey.length === 3) {
      dispatch(updateSelectedNavItem(NAV_ITEMS.EDIT_FOOTER_LINKS));
    }
    setFieldKey(newKey);
  };
  const handleOccurenceTranslationUpdate = (keyPath, value) => {
    const {
      updateKnowledgeBaseTranslations,
      translatedContent
    } = props;
    const translations = translatedContent.get('data');
    const prevValue = getModuleValues().getIn(keyPath);
    if (Array.isArray(value)) {
      if (value.length > prevValue.length) value.forEach(item => {
        if (item && !item.uuid) {
          item.uuid = uuidv4();
        }
      });
      const updatedTranslationData = syncLanguageLinkTranslations(translations, {
        prevValue,
        value,
        keyPath
      });
      updateKnowledgeBaseTranslations(updatedTranslationData);
    } else {
      const primaryTranslationPathKey = translations.keySeq().toArray()[0];
      const itemPath = keyPath.slice(1, keyPath.length - 1);
      const itemValuePath = keyPath.slice(1, keyPath.length);
      itemPath.unshift(primaryTranslationPathKey);
      const itemToBeUpdated = translations.getIn(getProperTranslationKeyPath(itemPath));
      if (itemToBeUpdated) {
        itemValuePath.unshift(primaryTranslationPathKey);
        const updatedTranslations = translations.setIn(getProperTranslationKeyPath(itemValuePath), value);
        const occurenceKeyPath = getProperTranslationKeyPath(itemPath.splice(0, itemPath.length - 1));
        updateKnowledgeBaseTranslations(syncLanguageLinkTranslations(updatedTranslations, {
          prevValue: translations.getIn(occurenceKeyPath).toJS(),
          value: updatedTranslations.getIn(occurenceKeyPath).toJS(),
          keyPath: occurenceKeyPath
        }));
      }
    }
  };
  const handleUpdateField = (key, value, updatedModule, errors) => {
    const arrayKeyChildIndex = key.indexOf(key.find(k => typeof k === 'number'));
    if (arrayKeyChildIndex !== -1) {
      // The reducer uses immutable's setIn, which can't handle setting inside native arrays
      // If our keyPath contains a nested array, we adjust the update to set the entire updated array
      // as provided in our third callback argument by the EditForm
      const keyPathUpToArray = key.slice(0, arrayKeyChildIndex);
      const updatedArray = keyPathUpToArray.reduce((node, nextKey) => node[nextKey], updatedModule);
      handleUpdateTemplateEditorForm({
        changes: [{
          keyPath: keyPathUpToArray,
          value: updatedArray
        }],
        fieldPath: key,
        navItem: selectedNavItem.type,
        errors
      });
      return;
    }
    handleUpdateTemplateEditorForm({
      changes: [{
        keyPath: key,
        value
      }],
      fieldPath: key,
      navItem: selectedNavItem.type,
      errors
    });
    if (key.some(keyVal => typeof keyVal === 'number') || Array.isArray(value)) {
      // handleOccurenceTranslationUpdate(key, value);
    }
  };
  const handleUpdate = (keyPath, value, module) => {
    const targetField = getFieldDefinition(keyPath, selectedNavItem.type);
    const prevValue = getModuleValues().getIn(keyPath);
    const shouldShowModal = shouldShowTranslationModal();
    trackFieldChange(targetField, value, prevValue);
    const errors = validateField(targetField, value, keyPath, getModuleValues());
    const isOccurence = keyPath.some(keyVal => typeof keyVal === 'number') || Array.isArray(value);
    if (isOccurence) {
      if (Array.isArray(value) && value.length < prevValue.length && shouldShowModal) {
        setShowDeletePrompt(shouldShowModal);
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'any[]' is not assignable to type 'null'.
        setOnDeleteInfo({
          keyPath,
          value,
          module,
          errors,
          prevValue
        });
      }
      handleOccurenceTranslationUpdate(keyPath, value);
    }
    return handleUpdateField(keyPath, value, module, errors);
  };
  const onDeleteModalConfirm = (shouldDisableModal = false) => {
    const {
      keyPath,
      value,
      module,
      errors
    } = onDeleteInfo;
    const shouldShowModal = shouldShowTranslationModal();
    setShowDeletePrompt(shouldShowModal);
    handleOccurenceTranslationUpdate(keyPath, value);
    handleUpdateField(keyPath, value, module, errors);
    if (shouldDisableModal) {
      setItem('hideTranslationModal', 'true');
    }
  };
  const closeModal = () => {
    // Revert any changes in the EditForm then close modal.
    const {
      keyPath,
      prevValue: oldValue,
      module
    } = onDeleteInfo;
    if (oldValue) {
      handleUpdate(keyPath, oldValue, module);
    }
    setShowDeletePrompt(false);
  };
  const options = {
    hasSupportForm: connectedForms.length > 0,
    shouldShowCustomerPortalLinkToggle,
    shouldShowSignInLinkToggle,
    supportFormId: templateSettings.getIn(SUPPORT_FORM_ID_SETTING_KEY_PATH),
    indexTemplatePath
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [hasHeader && /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(ContentFormHeader, {}), /*#__PURE__*/_jsx(H4, {
        className: "p-x-6 p-top-5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `knowledgeContentUI.templateEditor.sidebar.navItems.${selectedNavItem.type}.breadcrumbText`
        })
      }), isViewingLanguageVariation && /*#__PURE__*/_jsx(TemplateEditorSidebarAlert, {
        selectedLanguage: selectedLanguage,
        masterLanguage: masterLanguage,
        handleTranslateClick: handleTranslateClick
      })]
    }), !hasHeader && isViewingLanguageVariation && /*#__PURE__*/_jsx(TemplateEditorSidebarAlert, {
      selectedLanguage: selectedLanguage,
      masterLanguage: masterLanguage,
      handleTranslateClick: handleTranslateClick
    }), showDeletePrompt && /*#__PURE__*/_jsx(ConfirmDeletionModal, {
      onClose: closeModal,
      onDeleteConfirm: onDeleteModalConfirm
    }), /*#__PURE__*/_jsx(StyledEditForm, {
      contentCategoryName: "",
      componentsByType: TemplateEditorFieldMap,
      fieldKey: fieldKey,
      flush: false,
      hideBackLink: true,
      id: "template-settings-form",
      moduleSpec: getModuleSpec(selectedNavItem.type, options),
      onFieldKeyChange: handleFieldKeyChange,
      module: getModuleValues(),
      onUpdate: handleUpdate,
      readOnly: isViewingLanguageVariation
    })]
  });
}
TemplateEditorForm.defaultProps = {
  hasHeader: true
};
TemplateEditorForm.propTypes = {
  dispatchDebouncedUpdateTemplateSettings: PropTypes.func.isRequired,
  fetchHasPrivateArticles: PropTypes.func.isRequired,
  fetchCustomerPortalSettings: PropTypes.func.isRequired,
  hasHeader: PropTypes.bool,
  masterLanguage: PropTypes.string.isRequired,
  selectedNavItem: PropTypes.object.isRequired,
  translatedContent: PropTypes.object,
  selectedLanguage: PropTypes.string.isRequired,
  handleTranslateClick: PropTypes.func.isRequired,
  updateKnowledgeBaseTranslations: PropTypes.func.isRequired
};
export default TemplateEditorForm;